import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Loader2, LogIn, UserPlus, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Dialog shown after an anonymous action (visit, participation) is submitted.
 * Behaviour:
 *  - userExists=true  -> propose login with prefilled email
 *  - userExists=false -> propose account creation, prefilled with submitted info.
 *                       After signup, backend backfills orphan demands and we toast
 *                       the linked_count.
 */
const PostAnonAccountPrompt = ({
  open,
  onOpenChange,
  userExists,
  prefillNom = '',
  prefillPrenom = '',
  prefillEmail = '',
  prefillTelephone = '',
}) => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [mode, setMode] = useState('initial'); // 'initial' | 'signup'
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setMode('initial');
    setPassword('');
    setConfirm('');
    setSubmitting(false);
    onOpenChange(false);
  };

  const handleGoLogin = () => {
    handleClose();
    navigate(`/auth?email=${encodeURIComponent(prefillEmail)}`);
  };

  const handleSignup = async () => {
    if (password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    if (password !== confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setSubmitting(true);
    try {
      const data = await register({
        email: prefillEmail,
        password,
        nom: prefillNom,
        prenom: prefillPrenom,
        telephone: prefillTelephone,
      });
      const linked = data?.linked_count || 0;
      toast.success(
        linked > 0
          ? `Compte créé. ${linked} demande${linked > 1 ? 's' : ''} liée${linked > 1 ? 's' : ''} à votre compte.`
          : 'Compte créé avec succès.'
      );
      handleClose();
    } catch (err) {
      toast.error(err.message || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="post-anon-account-prompt">
        {userExists ? (
          <>
            <DialogHeader>
              <DialogTitle>Un compte existe déjà</DialogTitle>
              <DialogDescription>
                L'adresse <strong>{prefillEmail}</strong> est déjà associée à un compte JAMM IMMO.
                Votre demande a été enregistrée et liée à ce compte. Connectez-vous pour la suivre.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose} data-testid="close-prompt-btn">
                Plus tard
              </Button>
              <Button onClick={handleGoLogin} data-testid="go-login-btn">
                <LogIn className="w-4 h-4 mr-2" /> Se connecter
              </Button>
            </DialogFooter>
          </>
        ) : mode === 'initial' ? (
          <>
            <DialogHeader>
              <DialogTitle>Demande envoyée avec succès</DialogTitle>
              <DialogDescription>
                Souhaitez-vous créer un compte avec <strong>{prefillEmail}</strong> pour suivre l'avancement de votre demande ?
                Vos demandes seront automatiquement liées à votre nouveau compte.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose} data-testid="skip-signup-btn">
                Non merci
              </Button>
              <Button onClick={() => setMode('signup')} data-testid="open-signup-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Créer mon compte
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Créer votre compte</DialogTitle>
              <DialogDescription>
                Compte pour <strong>{prefillPrenom} {prefillNom}</strong> ({prefillEmail})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="anon-password">Mot de passe (min. 6 caractères)</Label>
                <Input
                  id="anon-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="anon-password-input"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <Label htmlFor="anon-password-confirm">Confirmer le mot de passe</Label>
                <Input
                  id="anon-password-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="anon-password-confirm-input"
                  autoComplete="new-password"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode('initial')} disabled={submitting}>
                Retour
              </Button>
              <Button onClick={handleSignup} disabled={submitting} data-testid="confirm-signup-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Créer mon compte
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PostAnonAccountPrompt;
